import { ComponentWithHistory } from './types';

/**
 * The analysis engine names affected components in prose ("Hub Motor",
 * "propelers"), not by index — so replacing one means matching that name
 * back onto the BOM. Exact match first, then substring, then word-root
 * fuzzing for typos/plurals; -1 when nothing plausibly matches (callers
 * must treat that as "no one-click action", never guess).
 */
const clean = (str: string) =>
  str.toLowerCase().replace(/[^a-z0-9]/g, ' ').replace(/\s+/g, ' ').trim();

const fieldsOf = (c: any) => ({
  name: clean(c.name || c.rawName || c.componentName || c.title || ''),
  part: clean(c.partNumber || ''),
  norm: clean(c.ontologyMapping?.normalizedType || ''),
  desc: clean(c.description || ''),
});

export const findComponentIndex = (
  components: ComponentWithHistory[],
  targetName?: string,
): number => {
  if (!targetName || !components || components.length === 0) return -1;
  const target = clean(targetName);
  if (!target) return -1;

  let idx = components.findIndex((c) => {
    const f = fieldsOf(c);
    return f.name === target || f.part === target || f.norm === target;
  });
  if (idx !== -1) return idx;

  idx = components.findIndex((c) => {
    const f = fieldsOf(c);
    if (f.name && (f.name.includes(target) || target.includes(f.name))) return true;
    if (f.part && (f.part.includes(target) || target.includes(f.part))) return true;
    if (f.norm && (f.norm.includes(target) || target.includes(f.norm))) return true;
    if (f.desc && f.desc.includes(target)) return true;
    return false;
  });
  if (idx !== -1) return idx;

  const words = target.split(' ').filter((w) => w.length >= 4);
  if (words.length === 0) return -1;
  const rootOf = (word: string) => (word.length > 5 ? word.slice(0, 5) : word.slice(0, 4));

  idx = components.findIndex((c) => {
    const f = fieldsOf(c);
    const fullText = [f.name, f.part, f.norm, f.desc].join(' ');
    return words.every((word) => fullText.includes(rootOf(word)));
  });
  if (idx !== -1) return idx;

  return components.findIndex((c) => {
    const f = fieldsOf(c);
    const fullText = [f.name, f.norm].join(' ');
    return words.some((word) => fullText.includes(rootOf(word)));
  });
};
